import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Gift,
  TrendingUp,
  Users,
  History,
  Award,
  Star,
  ChevronRight,
  Share2,
  Copy,
  Check,
  Loader2,
  FileCheck,
  ExternalLink,
} from 'lucide-react';
import { copyToClipboard } from '../../utils/clipboard';
import { toast } from 'sonner';
import { ShareOptionsModal } from '../shared/ShareOptionsModal';
import { RewardsWalletModals } from './RewardsWalletModals';
import { RewardsActionsSection } from './RewardsActionsSection';
import { CommunityRewards } from './CommunityRewards';
import { ClaimsView } from './ClaimsView';
import { DashboardHeaderControls } from '../DashboardHeaderControls';
import { useRewardsBalance } from '@/hooks/useRewardsBalance';
import {
  getEarningActions,
  EarningAction,
  getRewardHistory,
  RewardTransaction,
  getRedeemRewards,
  redeemReward as redeemRewardApi,
  type RedeemRewardItem,
  submitEarningAction,
  completeEarningAction,
} from '@/api/rewards';
import {
  getReferrals,
  getReferralConfigFromApi,
  ReferralStats,
  Referral,
  type ReferralConfigResponse,
} from '@/api/referrals';
import { saveReferralConfigToStorage } from '@shared/referralConfig';
import { usePets } from '../../context/PetsContext';
import { useTranslation } from 'react-i18next';
import { getSolanaClusterParam } from '@/utils/solanaExplorer';

// Fallback set for actions created before isAutoReward DB field existed
const AUTO_REWARD_ACTION_KEYS_FALLBACK = new Set(['r2', 'r3', 'r10']);
// Action key that should navigate to the Referrals tab on Start
const REFERRAL_ACTION_KEY = 'r8';

interface RoleBasedRewardsDashboardProps {
  userType: 'owner' | 'professional';
  userId: string;
  professionalType?:
    | 'veterinarian'
    | 'groomer'
    | 'walker'
    | 'trainer'
    | 'boarding'
    | 'daycare'
    | 'other';
  businessName?: string;
}

// Role-specific configuration — display text comes from t('rewards.roleName.*'), t('rewards.actions.*'), t('rewards.redemptionTitle.*'), t('rewards.redemptionDesc.*')
const ROLE_CONFIGS = {
  veterinarian: {
    roleNameKey: 'veterinarian',
    name: 'Veterinarian',
    icon: '🏥',
    color: 'from-red-500 to-pink-500',
    earningActions: [
      {
        id: '1',
        actionKey: 'completeProfile',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        actionKey: 'acceptEmergencyBooking',
        action: 'Accept emergency booking',
        points: 50,
        icon: '🚨',
        completed: false,
      },
      {
        id: '3',
        actionKey: 'completeConsultation',
        action: 'Complete consultation',
        points: 30,
        icon: '✅',
        completed: true,
      },
      {
        id: '4',
        actionKey: 'issueMedicalCertificate',
        action: 'Issue medical certificate',
        points: 25,
        icon: '📋',
        completed: false,
      },
      {
        id: '5',
        actionKey: 'receive5StarReview',
        action: 'Receive 5-star review',
        points: 75,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        actionKey: 'postMedicalArticle',
        action: 'Post medical article',
        points: 40,
        icon: '📝',
        completed: false,
      },
      {
        id: '7',
        actionKey: 'updateVaccinationRecord',
        action: 'Update vaccination record',
        points: 20,
        icon: '💉',
        completed: false,
      },
      {
        id: '8',
        actionKey: 'completeSurgicalProcedure',
        action: 'Complete surgical procedure',
        points: 100,
        icon: '🏥',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        titleKey: 'zeroCommissionWeek',
        descriptionKey: 'zeroCommissionWeek',
        title: 'Zero Commission Week',
        description: '0% fees for 7 days',
        points: 3000,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '2',
        titleKey: 'topVeterinarianBadge',
        descriptionKey: 'topVeterinarianBadge',
        title: 'Top Veterinarian Badge',
        description: 'Featured as top vet for 30 days',
        points: 2500,
        icon: '🏆',
        type: 'featured',
      },
      {
        id: '3',
        titleKey: 'emergencyPriority',
        descriptionKey: 'emergencyPriority',
        title: 'Emergency Priority',
        description: 'Priority in emergency searches',
        points: 2000,
        icon: '🚨',
        type: 'priority',
      },
      {
        id: '4',
        titleKey: 'advancedMedicalAnalytics',
        descriptionKey: 'advancedMedicalAnalytics',
        title: 'Advanced Medical Analytics',
        description: '90 days premium analytics',
        points: 1500,
        icon: '📊',
        type: 'analytics',
      },
      {
        id: '5',
        titleKey: 'telemedicineCredits',
        descriptionKey: 'telemedicineCredits',
        title: 'Telemedicine Credits',
        description: '50 free video consultations',
        points: 1000,
        icon: '📹',
        type: 'service',
      },
      {
        id: '6',
        titleKey: 'marketingBoost',
        descriptionKey: 'marketingBoost',
        title: 'Marketing Boost',
        description: 'Featured in "Find a Vet" for 14 days',
        points: 800,
        icon: '📢',
        type: 'marketing',
      },
    ],
  },
  groomer: {
    roleNameKey: 'groomer',
    name: 'Groomer',
    icon: '✂️',
    color: 'from-purple-500 to-indigo-500',
    earningActions: [
      {
        id: '1',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        action: 'Accept booking',
        points: 15,
        icon: '✓',
        completed: true,
      },
      {
        id: '3',
        action: 'Complete grooming session',
        points: 30,
        icon: '✅',
        completed: true,
      },
      {
        id: '4',
        action: 'Upload before/after photos',
        points: 35,
        icon: '📸',
        completed: false,
      },
      {
        id: '5',
        action: 'Receive 5-star review',
        points: 60,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        action: 'Post grooming tips',
        points: 25,
        icon: '💡',
        completed: false,
      },
      {
        id: '7',
        action: 'Complete breed-specific groom',
        points: 40,
        icon: '🐾',
        completed: false,
      },
      {
        id: '8',
        action: 'Handle difficult coat type',
        points: 50,
        icon: '✂️',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        title: 'Featured Groomer',
        description: 'Top placement for 30 days',
        points: 2000,
        icon: '⭐',
        type: 'featured',
      },
      {
        id: '2',
        title: 'Reduced Booking Fees',
        description: '50% off fees for 30 days',
        points: 1800,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '3',
        title: 'Portfolio Showcase',
        description: 'Premium gallery on profile',
        points: 1200,
        icon: '📸',
        type: 'feature',
      },
      {
        id: '4',
        title: 'Social Media Boost',
        description: 'Work featured on Petso social',
        points: 1000,
        icon: '📱',
        type: 'marketing',
      },
      {
        id: '5',
        title: 'Grooming Kit Discount',
        description: '25% off partner supplies',
        points: 800,
        icon: '🎁',
        type: 'partner',
      },
      {
        id: '6',
        title: 'Priority Scheduling',
        description: 'Early access to bookings',
        points: 600,
        icon: '⏰',
        type: 'priority',
      },
    ],
  },
  walker: {
    roleNameKey: 'walker',
    name: 'Pet Walker',
    icon: '🚶',
    color: 'from-green-500 to-teal-500',
    earningActions: [
      {
        id: '1',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        action: 'Accept walk booking',
        points: 10,
        icon: '✓',
        completed: true,
      },
      {
        id: '3',
        action: 'Complete walk',
        points: 20,
        icon: '🚶',
        completed: true,
      },
      {
        id: '4',
        action: 'Share walk photos/videos',
        points: 30,
        icon: '📸',
        completed: false,
      },
      {
        id: '5',
        action: 'Receive 5-star review',
        points: 50,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        action: 'Complete group walk',
        points: 35,
        icon: '👥',
        completed: false,
      },
      {
        id: '7',
        action: 'Walk in bad weather',
        points: 40,
        icon: '🌧️',
        completed: false,
      },
      {
        id: '8',
        action: 'Achieve 100 walks milestone',
        points: 200,
        icon: '🏆',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        title: 'Top Walker Badge',
        description: 'Featured walker for 30 days',
        points: 1500,
        icon: '🏆',
        type: 'featured',
      },
      {
        id: '2',
        title: 'Zero Commission Month',
        description: '0% fees for 30 days',
        points: 2000,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '3',
        title: 'GPS Tracker Premium',
        description: 'Free premium tracking for 60 days',
        points: 1000,
        icon: '📍',
        type: 'service',
      },
      {
        id: '4',
        title: 'Walker Gear Package',
        description: 'Free leashes, bags, supplies',
        points: 800,
        icon: '🎒',
        type: 'partner',
      },
      {
        id: '5',
        title: 'Route Planning Pro',
        description: 'Advanced route analytics',
        points: 700,
        icon: '🗺️',
        type: 'analytics',
      },
      {
        id: '6',
        title: 'Priority Bookings',
        description: 'First access to new clients',
        points: 500,
        icon: '⏰',
        type: 'priority',
      },
    ],
  },
  trainer: {
    roleNameKey: 'trainer',
    name: 'Trainer / Behaviourist',
    icon: '🎓',
    color: 'from-yellow-500 to-orange-500',
    earningActions: [
      {
        id: '1',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        action: 'Accept training booking',
        points: 20,
        icon: '✓',
        completed: true,
      },
      {
        id: '3',
        action: 'Complete training session',
        points: 40,
        icon: '✅',
        completed: true,
      },
      {
        id: '4',
        action: 'Upload training progress video',
        points: 50,
        icon: '🎥',
        completed: false,
      },
      {
        id: '5',
        action: 'Receive 5-star review',
        points: 70,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        action: 'Post training tip/guide',
        points: 35,
        icon: '📚',
        completed: false,
      },
      {
        id: '7',
        action: 'Complete behavior assessment',
        points: 60,
        icon: '🔍',
        completed: false,
      },
      {
        id: '8',
        action: 'Graduate a student pet',
        points: 150,
        icon: '🎓',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        title: 'Certified Trainer Badge',
        description: 'Top trainer placement for 60 days',
        points: 2500,
        icon: '🏆',
        type: 'featured',
      },
      {
        id: '2',
        title: 'Commission Free Training',
        description: '0% fees for 45 days',
        points: 2200,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '3',
        title: 'Video Course Platform',
        description: 'Host online courses on Petso',
        points: 1800,
        icon: '🎥',
        type: 'feature',
      },
      {
        id: '4',
        title: 'Training Equipment Discount',
        description: '30% off partner equipment',
        points: 1200,
        icon: '🎁',
        type: 'partner',
      },
      {
        id: '5',
        title: 'Progress Tracking Pro',
        description: 'Advanced client tracking tools',
        points: 1000,
        icon: '📊',
        type: 'analytics',
      },
      {
        id: '6',
        title: 'Webinar Hosting',
        description: 'Host 3 free webinars on Petso',
        points: 800,
        icon: '🎤',
        type: 'service',
      },
    ],
  },
  boarding: {
    roleNameKey: 'boarding',
    name: 'Boarding / Pet Hotel',
    icon: '🏠',
    color: 'from-blue-500 to-cyan-500',
    earningActions: [
      {
        id: '1',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        action: 'Accept boarding booking',
        points: 25,
        icon: '✓',
        completed: true,
      },
      {
        id: '3',
        action: 'Complete overnight stay',
        points: 35,
        icon: '🌙',
        completed: true,
      },
      {
        id: '4',
        action: 'Send daily photo update',
        points: 20,
        icon: '📸',
        completed: false,
      },
      {
        id: '5',
        action: 'Receive 5-star review',
        points: 80,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        action: 'Complete long-term stay (7+ days)',
        points: 100,
        icon: '📅',
        completed: false,
      },
      {
        id: '7',
        action: 'Handle special needs pet',
        points: 60,
        icon: '❤️',
        completed: false,
      },
      {
        id: '8',
        action: 'Maintain perfect safety record',
        points: 200,
        icon: '🛡️',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        title: 'Premium Facility Badge',
        description: 'Top boarding facility for 60 days',
        points: 3000,
        icon: '🏆',
        type: 'featured',
      },
      {
        id: '2',
        title: 'Zero Booking Fees',
        description: '0% fees for 60 days',
        points: 2800,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '3',
        title: 'Live Camera System',
        description: 'Free live streaming for 90 days',
        points: 2000,
        icon: '📹',
        type: 'service',
      },
      {
        id: '4',
        title: 'Facility Showcase',
        description: 'Professional facility photography',
        points: 1500,
        icon: '📸',
        type: 'marketing',
      },
      {
        id: '5',
        title: 'Occupancy Analytics',
        description: 'Premium booking analytics',
        points: 1200,
        icon: '📊',
        type: 'analytics',
      },
      {
        id: '6',
        title: 'Supply Discount',
        description: '20% off bedding & supplies',
        points: 900,
        icon: '🎁',
        type: 'partner',
      },
    ],
  },
  daycare: {
    roleNameKey: 'daycare',
    name: 'Daycare',
    icon: '🎪',
    color: 'from-pink-500 to-rose-500',
    earningActions: [
      {
        id: '1',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        action: 'Accept daycare booking',
        points: 15,
        icon: '✓',
        completed: true,
      },
      {
        id: '3',
        action: 'Complete daycare session',
        points: 25,
        icon: '✅',
        completed: true,
      },
      {
        id: '4',
        action: 'Share activity photos/videos',
        points: 30,
        icon: '📸',
        completed: false,
      },
      {
        id: '5',
        action: 'Receive 5-star review',
        points: 60,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        action: 'Host special event/activity',
        points: 50,
        icon: '🎉',
        completed: false,
      },
      {
        id: '7',
        action: 'Complete group socialization',
        points: 35,
        icon: '👥',
        completed: false,
      },
      {
        id: '8',
        action: 'Monthly safety certification',
        points: 100,
        icon: '🛡️',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        title: 'Top Daycare Badge',
        description: 'Featured daycare for 45 days',
        points: 2200,
        icon: '🏆',
        type: 'featured',
      },
      {
        id: '2',
        title: 'Reduced Fees',
        description: '50% off fees for 45 days',
        points: 2000,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '3',
        title: 'Activity Cam System',
        description: 'Free camera system for 60 days',
        points: 1800,
        icon: '📹',
        type: 'service',
      },
      {
        id: '4',
        title: 'Social Media Features',
        description: 'Daily features on Petso social',
        points: 1200,
        icon: '📱',
        type: 'marketing',
      },
      {
        id: '5',
        title: 'Attendance Analytics',
        description: 'Premium attendance tracking',
        points: 1000,
        icon: '📊',
        type: 'analytics',
      },
      {
        id: '6',
        title: 'Play Equipment Discount',
        description: '25% off toys & equipment',
        points: 700,
        icon: '🎁',
        type: 'partner',
      },
    ],
  },
  other: {
    name: 'Pet Services',
    icon: '🐾',
    color: 'from-gray-500 to-slate-500',
    earningActions: [
      {
        id: '1',
        action: 'Complete profile',
        points: 100,
        icon: '👤',
        completed: true,
      },
      {
        id: '2',
        action: 'Accept booking',
        points: 15,
        icon: '✓',
        completed: true,
      },
      {
        id: '3',
        action: 'Complete service',
        points: 30,
        icon: '✅',
        completed: true,
      },
      {
        id: '4',
        action: 'Upload service photos',
        points: 25,
        icon: '📸',
        completed: false,
      },
      {
        id: '5',
        action: 'Receive 5-star review',
        points: 50,
        icon: '⭐',
        completed: false,
      },
      {
        id: '6',
        action: 'Post service tips',
        points: 20,
        icon: '💡',
        completed: false,
      },
    ],
    redemptions: [
      {
        id: '1',
        title: 'Featured Service Provider',
        description: 'Top placement for 30 days',
        points: 1800,
        icon: '⭐',
        type: 'featured',
      },
      {
        id: '2',
        title: 'Reduced Fees',
        description: '40% off fees for 30 days',
        points: 1500,
        icon: '💰',
        type: 'fee',
      },
      {
        id: '3',
        title: 'Marketing Boost',
        description: 'Featured in category searches',
        points: 1000,
        icon: '📢',
        type: 'marketing',
      },
      {
        id: '4',
        title: 'Service Analytics',
        description: 'Premium business analytics',
        points: 800,
        icon: '📊',
        type: 'analytics',
      },
      {
        id: '5',
        title: 'Partner Discounts',
        description: '20% off partner supplies',
        points: 600,
        icon: '🎁',
        type: 'partner',
      },
    ],
  },
};

export function RoleBasedRewardsDashboard({
  userType,
  userId: _userId,
  professionalType,
  businessName,
}: RoleBasedRewardsDashboardProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    'earn' | 'redeem' | 'referrals' | 'community' | 'claims' | 'history'
  >('earn');
  const [copied, setCopied] = useState(false);
  const [linkCopiedWhich, setLinkCopiedWhich] = useState<
    'webapp' | 'pro' | null
  >(null);
  const [referralConfig, setReferralConfig] =
    useState<ReferralConfigResponse | null>(null);
  const [fetchedEarningActions, setFetchedEarningActions] = useState<
    EarningAction[]
  >([]);
  const [isLoadingActions, setIsLoadingActions] = useState(true);

  // History state
  const [historyTransactions, setHistoryTransactions] = useState<
    RewardTransaction[]
  >([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const [historyOffset, setHistoryOffset] = useState(0);
  const HISTORY_PAGE_SIZE = 10;

  // Referral state
  const [referralCode, setReferralCode] = useState<string>('');
  const [referralStats, setReferralStats] = useState<ReferralStats>({
    total: 0,
    pending: 0,
    completed: 0,
    totalPointsEarned: 0,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future referrals list UI
  const [_referralsList, setReferralsList] = useState<Referral[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future loading state UI
  const [_isLoadingReferrals, setIsLoadingReferrals] = useState(false);

  // Fetch pets data to determine completed actions
  const { pets } = usePets();

  // Fetch earning actions from API
  useEffect(() => {
    const fetchEarningActions = async () => {
      try {
        setIsLoadingActions(true);
        const response = await getEarningActions(userType);
        setFetchedEarningActions(response.earningActions);
      } catch (error) {
        console.error('Failed to fetch earning actions:', error);
        // Fallback will be handled by empty array
      } finally {
        setIsLoadingActions(false);
      }
    };

    fetchEarningActions();
  }, [userType]);

  // Fetch history when history tab is active
  useEffect(() => {
    if (
      activeTab === 'history' &&
      historyTransactions.length === 0 &&
      !isLoadingHistory
    ) {
      fetchHistory(0);
    }
  }, [activeTab]);

  // Fetch referrals and referral config from API (Admin → Rewards → Referrals). Single source of truth: backend.
  const fetchReferralsAndConfig = useCallback(async () => {
    try {
      setIsLoadingReferrals(true);
      const [refResponse, config] = await Promise.all([
        getReferrals('all', 20, 0),
        getReferralConfigFromApi(),
      ]);
      setReferralCode(refResponse.referralCode || '');
      setReferralStats(refResponse.stats);
      setReferralsList(refResponse.referrals);
      if (config) {
        setReferralConfig(config);
        saveReferralConfigToStorage(config);
      }
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
    } finally {
      setIsLoadingReferrals(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'referrals' || referralCode === '') {
      fetchReferralsAndConfig();
    }
  }, [activeTab, fetchReferralsAndConfig]);

  // When user returns to this tab (e.g. after saving in Admin), refetch config so shareable links stay in sync
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeTab === 'referrals') {
        getReferralConfigFromApi().then((config) => {
          if (config) {
            setReferralConfig(config);
            saveReferralConfigToStorage(config);
          }
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [activeTab]);

  const fetchHistory = async (offset: number) => {
    try {
      setIsLoadingHistory(true);
      const response = await getRewardHistory(HISTORY_PAGE_SIZE, offset);
      const rawTransactions = Array.isArray(response?.transactions)
        ? response.transactions
        : [];
      const normalizedTransactions: RewardTransaction[] = rawTransactions
        .filter(
          (tx): tx is Partial<RewardTransaction> =>
            !!tx && typeof tx === 'object'
        )
        .map((tx, index) => ({
          id: tx.id || `history-${offset}-${index}`,
          type:
            tx.type === 'earned' ||
            tx.type === 'redeemed' ||
            tx.type === 'claimed' ||
            tx.type === 'transferred'
              ? tx.type
              : 'earned',
          actionKey: tx.actionKey,
          description:
            tx.description ||
            t('rewards.unknownTransaction', 'Reward transaction'),
          points: Number(tx.points ?? 0),
          transactionSignature: tx.transactionSignature,
          createdAt: tx.createdAt || new Date().toISOString(),
        }));
      if (offset === 0) {
        setHistoryTransactions(normalizedTransactions);
      } else {
        setHistoryTransactions((prev) => [...prev, ...normalizedTransactions]);
      }
      const hasMore =
        typeof response?.hasMore === 'boolean'
          ? response.hasMore
          : normalizedTransactions.length >= HISTORY_PAGE_SIZE;
      setHistoryHasMore(hasMore);
      setHistoryOffset(offset + normalizedTransactions.length);
    } catch (error) {
      console.error('Failed to fetch reward history:', error);
      if (offset === 0) {
        setHistoryTransactions([]);
      }
      setHistoryHasMore(false);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleLoadMoreHistory = () => {
    if (!isLoadingHistory && historyHasMore) {
      fetchHistory(historyOffset);
    }
  };

  // Format date for display
  const formatHistoryDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return t('rewards.minAgo', { n: diffMins });
    } else if (diffHours < 24) {
      return t('rewards.hourAgo', { n: diffHours });
    } else if (diffDays < 7) {
      return t('rewards.dayAgo', { n: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  // Calculate completed actions based on actual user data
  const computedCompletedActions = useMemo(() => {
    const completed: string[] = [];

    // Check if user has at least one pet created
    if (pets.length > 0) {
      completed.push('create_pet_profile');
    }

    // Check if any pet has a DID created
    const hasPetWithDid = pets.some((pet) => pet.did && pet.did.trim() !== '');
    if (hasPetWithDid) {
      completed.push('create_pet_did');
    }

    // Check if any pet has a complete profile (has all main fields filled)
    const hasCompletePetProfile = pets.some(
      (pet) =>
        pet.name && pet.species && pet.breed && pet.dateOfBirth && pet.gender
    );
    if (hasCompletePetProfile) {
      completed.push('complete_pet_profile');
    }

    return completed;
  }, [pets]);

  // Fetch real token balance from Solana
  const {
    uiBalance,
    isLoading: isBalanceLoading,
    chain,
    walletAddress,
    createWallet,
    isCreatingWallet,
    claimableRewards,
    claimRewards,
    isClaiming,
    transferToWallet,
    isTransferring,
    refetch: refetchBalance,
  } = useRewardsBalance();
  const tokenBalance = parseFloat(uiBalance) || 0;
  // Total points = on-chain wallet balance only (claimed tokens)
  // Unclaimed rewards are shown separately in the Claims section
  const totalPoints = tokenBalance;

  // Manual completions (for actions completed during this session)
  const [manualCompletedActions, setManualCompletedActions] = useState<
    string[]
  >([]);
  const [redeemedRewards, setRedeemedRewards] = useState<string[]>([]);
  const [apiRedeemRewards, setApiRedeemRewards] = useState<RedeemRewardItem[]>(
    []
  );
  const [redeemRewardsLoading, setRedeemRewardsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future share menu UI
  const [_shareMenuOpen, _setShareMenuOpen] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalContent, setShareModalContent] = useState({
    title: '',
    message: '',
    url: '',
  });

  // Transfer states
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState<
    'claim' | 'send' | 'transfer' | null
  >(null);
  const [sendAmount, setSendAmount] = useState('');
  const [sendRecipient, setSendRecipient] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [selectedWalletType, setSelectedWalletType] = useState<
    'custodial' | 'external'
  >('custodial');
  const [externalWalletAddress, setExternalWalletAddress] = useState('');

  // Get role-specific configuration
  const roleConfig =
    userType === 'professional' && professionalType
      ? ROLE_CONFIGS[professionalType]
      : null;

  // Fetch redeem rewards from API for owners (synced from Admin)
  useEffect(() => {
    if (userType !== 'owner') return;
    let cancelled = false;
    setRedeemRewardsLoading(true);
    getRedeemRewards()
      .then((res) => {
        const raw = res.redeemRewards ?? [];
        if (!cancelled) {
          setApiRedeemRewards(
            raw.map((r: RedeemRewardItem) => ({
              ...r,
              discountCode: r.discountCode ?? r.discount_code ?? null,
              discountPercent: r.discountPercent ?? r.discount_percent ?? null,
            }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setApiRedeemRewards([]);
      })
      .finally(() => {
        if (!cancelled) setRedeemRewardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userType]);

  // Points available for redemption: owner uses claimableRewards (API deducts from this)
  const pointsAvailableForRedeem =
    userType === 'owner' ? (claimableRewards ?? 0) : Math.floor(tokenBalance);

  // Tier thresholds (must match pointsToNextTier logic)
  const TIER_SILVER = 1000;
  const TIER_GOLD = 5000;
  const TIER_PLATINUM = 10000;

  // Progress within current tier toward next (0–100%). Bronze→Silver: 0–1000; Silver→Gold: 1000–5000; Gold→Platinum: 5000–10000.
  // Uses totalPoints (on-chain + unclaimed) so progress bar reflects all earned rewards
  const tierProgressPercent =
    totalPoints >= TIER_PLATINUM
      ? 100
      : totalPoints >= TIER_GOLD
        ? Math.min(
            100,
            ((totalPoints - TIER_GOLD) / (TIER_PLATINUM - TIER_GOLD)) * 100
          )
        : totalPoints >= TIER_SILVER
          ? Math.min(
              100,
              ((totalPoints - TIER_SILVER) / (TIER_GOLD - TIER_SILVER)) * 100
            )
          : Math.min(100, (totalPoints / TIER_SILVER) * 100);

  // User data with real referral stats
  const userData = {
    points: totalPoints,
    tier:
      totalPoints >= TIER_PLATINUM
        ? 'Platinum'
        : totalPoints >= TIER_GOLD
          ? 'Gold'
          : totalPoints >= TIER_SILVER
            ? 'Silver'
            : 'Bronze',
    tierProgress: tierProgressPercent,
    nextTier:
      totalPoints >= TIER_PLATINUM
        ? 'Platinum'
        : totalPoints >= TIER_GOLD
          ? 'Platinum'
          : totalPoints >= TIER_SILVER
            ? 'Gold'
            : 'Silver',
    pointsToNextTier:
      totalPoints >= TIER_PLATINUM
        ? 0
        : totalPoints >= TIER_GOLD
          ? TIER_PLATINUM - totalPoints
          : totalPoints >= TIER_SILVER
            ? TIER_GOLD - totalPoints
            : TIER_SILVER - totalPoints,
    referralCode: referralCode || 'Loading...',
    totalReferrals: referralStats.total,
    pendingReferrals: referralStats.pending,
    completedReferrals: referralStats.completed,
  };

  const tierColors = {
    Bronze: '#CD7F32',
    Silver: '#C0C0C0',
    Gold: '#FFD700',
    Platinum: '#936FEF',
  };

  const handleCopyReferralCode = async () => {
    await copyToClipboard(userData.referralCode);
    setCopied(true);
    toast.success(t('rewards.referralCodeCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  // Build shareable links from Admin config (Admin → Rewards → Referrals); fallback to current origin if no config
  const buildLinkFromConfig = (
    linkConfig: { baseUrl: string; pathTemplate: string },
    code: string
  ): string => {
    const base =
      linkConfig.baseUrl.trim() ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    const path = (linkConfig.pathTemplate || '/join?ref={code}').replace(
      /\{code\}/g,
      encodeURIComponent(code)
    );
    const pathStart = path.startsWith('/') ? path : `/${path}`;
    return base.replace(/\/$/, '') + pathStart;
  };
  const defaultLink = { baseUrl: '', pathTemplate: '/join?ref={code}' };
  const webappLinkUrl = buildLinkFromConfig(
    referralConfig?.webappLink ?? defaultLink,
    userData.referralCode
  );
  const petsoProLinkUrl = buildLinkFromConfig(
    referralConfig?.petsoProLink ?? defaultLink,
    userData.referralCode
  );

  const handleCopyReferralLink = async (which: 'webapp' | 'pro') => {
    const url = which === 'webapp' ? webappLinkUrl : petsoProLinkUrl;
    await copyToClipboard(url);
    setLinkCopiedWhich(which);
    toast.success(t('rewards.shareLinkCopied'));
    setTimeout(() => setLinkCopiedWhich(null), 2000);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for share options integration
  const _handleShareReferralCode = () => {
    const shareText =
      userType === 'owner'
        ? t('rewards.joinPetsoOwner', { code: userData.referralCode })
        : t('rewards.joinPetsoProRole', { role: roleConfig?.name || 'professional', code: userData.referralCode });

    if (navigator.share) {
      navigator
        .share({
          title: t('rewards.joinPetso'),
          text: shareText,
        })
        .catch(() => {
          // Fallback to copy if share fails
          copyToClipboard(shareText);
          toast.success(t('rewards.shareTextCopied'));
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(shareText);
      toast.success(t('rewards.shareTextCopied'));
    }
  };

  // localStorage key for tracking in-progress earning actions (between Start and Submit)
  const EA_PROGRESS_KEY = 'petso_earning_actions_progress';

  const loadEAProgress = (): Record<string, { status: 'in-progress' }> => {
    try {
      const raw = localStorage.getItem(EA_PROGRESS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveEAProgress = (progress: Record<string, { status: 'in-progress' }>) => {
    try {
      localStorage.setItem(EA_PROGRESS_KEY, JSON.stringify(progress));
    } catch { /* ignore */ }
  };

  const [eaProgress, setEAProgress] = useState<Record<string, { status: 'in-progress' }>>(loadEAProgress);
  const [submittingActions, setSubmittingActions] = useState<Set<string>>(new Set());

  const handleStartAction = async (
    action: { actionKey: string; action: string; points: number; link?: string | null }
  ) => {
    // Referral Success → navigate to Referrals tab
    if (action.actionKey === REFERRAL_ACTION_KEY) {
      setActiveTab('referrals');
      return;
    }

    if (action.link) {
      // Action has a URL → open it in new tab, mark as in-progress
      window.open(action.link, '_blank');
      const updated = { ...eaProgress, [action.actionKey]: { status: 'in-progress' as const } };
      setEAProgress(updated);
      saveEAProgress(updated);
      toast.success(t('rewards.taskStarted', 'Task started!'), {
        description: t('rewards.completeAndReturn', 'Complete the task and return here to submit.'),
      });
    } else {
      // No URL → auto-complete via backend
      try {
        const result = await completeEarningAction(action.actionKey);
        setManualCompletedActions([...manualCompletedActions, action.actionKey]);
        toast.success(t('rewards.petsEarned', { points: result.pointsAwarded, action: action.action }), {
          description: t('rewards.pointsCredited'),
        });
        refetchBalance();
      } catch (err: unknown) {
        const msg = err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : t('rewards.failedToCompleteAction');
        if (msg === 'Action already completed') {
          setManualCompletedActions([...manualCompletedActions, action.actionKey]);
          toast.info(t('rewards.alreadyCompleted', 'Already completed!'));
        } else {
          toast.error(msg || t('rewards.failedToCompleteAction'));
        }
      }
    }
  };

  const handleSubmitAction = async (
    action: { actionKey: string; action: string; points: number }
  ) => {
    setSubmittingActions((prev) => new Set(prev).add(action.actionKey));
    try {
      await submitEarningAction({
        actionKey: action.actionKey,
        actionName: action.action,
        reward: action.points,
      });
      // Clear from in-progress localStorage
      const updated = { ...eaProgress };
      delete updated[action.actionKey];
      setEAProgress(updated);
      saveEAProgress(updated);
      // Refetch earning actions to get updated submission status from backend
      const response = await getEarningActions(userType);
      setFetchedEarningActions(response.earningActions);
      toast.success(t('rewards.submittedForReview', 'Submitted for review!'), {
        description: t('rewards.adminWillReview', 'An admin will review your submission.'),
      });
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : 'Failed to submit';
      if (msg === 'Already submitted for this action') {
        toast.info(t('rewards.alreadySubmitted', 'Already submitted!'));
      } else {
        toast.error(msg || 'Failed to submit');
      }
    } finally {
      setSubmittingActions((prev) => {
        const next = new Set(prev);
        next.delete(action.actionKey);
        return next;
      });
    }
  };

  const handleRedeemReward = async (reward: {
    id: string;
    title: string;
    points: number;
    type?: string;
    redirectLink?: string | null;
  }) => {
    const available = pointsAvailableForRedeem;
    if (available < reward.points) return;
    const fromApi = reward.type === 'api';
    if (fromApi) {
      try {
        const res = await redeemRewardApi({ rewardId: reward.id });
        setRedeemedRewards((prev) => [...prev, reward.id]);
        await refetchBalance();
        toast.success(t('rewards.rewardRedeemed', { title: reward.title }), {
          description: `${reward.points} PETS deducted. ${res.redirectLink ? 'Redirecting...' : ''}`,
        });
        if (res.redirectLink) {
          window.open(res.redirectLink, '_blank');
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response
                ?.data?.message
            : err instanceof Error
              ? err.message
              : t('rewards.redemptionFailed');
        toast.error(msg);
      }
      return;
    }
    setRedeemedRewards((prev) => [...prev, reward.id]);
    toast.success(t('rewards.rewardRedeemed', { title: reward.title }), {
      description: t('rewards.petsDeducted', { points: reward.points }),
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for share options integration
  const _handleShareReward = (reward: {
    id: string;
    title: string;
    points: number;
  }) => {
    const roleText = roleConfig ? `as a ${roleConfig.name}` : '';
    const shareText = `🎉 I just redeemed "${reward.title}" ${roleText} on Petso! Earning rewards for providing great pet care. 🐾 #Petso #PetCare #Rewards`;

    if (navigator.share) {
      navigator
        .share({
          title: t('rewards.petsoRewardRedeemed'),
          text: shareText,
        })
        .catch(() => {
          // Fallback to copy if share fails
          copyToClipboard(shareText);
          toast.success(t('rewards.shareTextCopied'));
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(shareText);
      toast.success(t('rewards.shareTextCopied'));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for share options integration
  const _handleShareToTwitter = (reward: {
    id: string;
    title: string;
    points: number;
  }) => {
    const roleText = roleConfig ? `as a ${roleConfig.name}` : '';
    const shareText = `🎉 I just redeemed "${reward.title}" ${roleText} on Petso! Earning rewards for providing great pet care. 🐾 #Petso #PetCare #Rewards`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    toast.success(t('rewards.openingTwitter'));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for share options integration
  const _handleShareToFacebook = (reward: {
    id: string;
    title: string;
    points: number;
  }) => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://petso.io')}&quote=${encodeURIComponent(`🎉 I just redeemed "${reward.title}" on Petso!`)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
    toast.success(t('rewards.openingFacebook'));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for share options integration
  const _handleShareToLinkedIn = (_reward: {
    id: string;
    title: string;
    points: number;
  }) => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://petso.io')}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
    toast.success(t('rewards.openingLinkedIn'));
  };

  // Combine computed and manual completed actions
  const allCompletedActions = useMemo(() => {
    return [
      ...new Set([...computedCompletedActions, ...manualCompletedActions]),
    ];
  }, [computedCompletedActions, manualCompletedActions]);

  // Owner earning actions (fallback) - actionKey matches backend (snake_case) for completed check; getActionLabel maps to t()
  const ownerEarningActions = useMemo(
    () => [
      {
        id: '1',
        actionKey: 'create_pet_profile',
        action: 'Create pet profile',
        points: 50,
        icon: '🐾',
        completed: allCompletedActions.includes('create_pet_profile'),
      },
      {
        id: '2',
        actionKey: 'create_pet_did',
        action: 'Create pet DID',
        points: 100,
        icon: '🆔',
        completed: allCompletedActions.includes('create_pet_did'),
      },
      {
        id: '3',
        actionKey: 'complete_pet_profile',
        action: 'Complete pet profile',
        points: 75,
        icon: '✅',
        completed: allCompletedActions.includes('complete_pet_profile'),
      },
      {
        id: '4',
        actionKey: 'book_appointment',
        action: 'Book appointment',
        points: 25,
        icon: '📅',
        completed: allCompletedActions.includes('book_appointment'),
      },
      {
        id: '5',
        actionKey: 'leave_review',
        action: 'Leave review',
        points: 20,
        icon: '⭐',
        completed: allCompletedActions.includes('leave_review'),
      },
      {
        id: '6',
        actionKey: 'post_on_social',
        action: 'Post on social',
        points: 10,
        icon: '📸',
        completed: allCompletedActions.includes('post_on_social'),
      },
      {
        id: '7',
        actionKey: 'daily_app_use',
        action: 'Daily app use',
        points: 5,
        icon: '🎯',
        completed: allCompletedActions.includes('daily_app_use'),
      },
    ],
    [allCompletedActions]
  );

  // Transform fetched earning actions for display, with fallback to hardcoded values
  const earningActions =
    fetchedEarningActions.length > 0
      ? fetchedEarningActions.map((action) => ({
          id: action.id,
          actionKey: action.actionKey,
          action: action.name,
          points: action.points,
          icon: action.icon,
          link: action.link ?? null,
          completed: action.finished || action.submissionStatus === 'approved' || allCompletedActions.includes(action.actionKey),
          submitted: action.submitted ?? false,
          submissionStatus: action.submissionStatus ?? null,
          inProgress: eaProgress[action.actionKey]?.status === 'in-progress',
          isAutoReward: action.isAutoReward ?? AUTO_REWARD_ACTION_KEYS_FALLBACK.has(action.actionKey),
          isReferral: action.actionKey === REFERRAL_ACTION_KEY,
        }))
      : roleConfig
        ? roleConfig.earningActions.map((action) => ({
            ...action,
            actionKey: action.id,
            completed: allCompletedActions.includes(action.id),
            link: null as string | null,
            submitted: false,
            submissionStatus: null as string | null,
            inProgress: false,
            isAutoReward: false,
            isReferral: false,
          }))
        : ownerEarningActions.map((a) => ({
            ...a,
            link: null as string | null,
            submitted: false,
            submissionStatus: null as string | null,
            inProgress: false,
            isAutoReward: false,
            isReferral: false,
          }));

  // Owner: redeem rewards from API only (managed in Admin → Rewards → Redeem Rewards)
  const ownerRedemptionsFromApi = useMemo(() => {
    if (apiRedeemRewards.length === 0) return [];
    return apiRedeemRewards.map((r) => ({
      id: r.id,
      titleKey: undefined,
      title: r.title,
      descriptionKey: undefined,
      description: r.description || '',
      points: r.petPointsRequired,
      icon: r.imageUrl ? undefined : '🎁',
      imageUrl: r.imageUrl ?? undefined,
      type: 'api' as const,
      remainingQuantity: r.remainingQuantity ?? undefined,
      redirectLink: r.redirectLink ?? undefined,
      discountCode: r.discountCode ?? undefined,
      discountPercent: r.discountPercent ?? undefined,
    }));
  }, [apiRedeemRewards]);

  const redemptions = roleConfig
    ? roleConfig.redemptions
    : ownerRedemptionsFromApi;

  // Referral stats for display
  const referralStatsDisplay = [
    {
      label: t('rewards.totalReferrals'),
      value: userData.totalReferrals,
      icon: Users,
      color: '#936FEF',
    },
    {
      label: t('rewards.pending'),
      value: userData.pendingReferrals,
      icon: TrendingUp,
      color: '#FFD700',
    },
    {
      label: t('rewards.completed'),
      value: userData.completedReferrals,
      icon: Check,
      color: '#10B981',
    },
  ];

  const snakeToCamel = (s: string) =>
    s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const getActionLabel = (action: { actionKey?: string; action: string }) => {
    if (action.actionKey && typeof action.actionKey === 'string') {
      const tKey = action.actionKey.includes('_')
        ? snakeToCamel(action.actionKey)
        : action.actionKey;
      const translated = t(`rewards.actions.${tKey}`);
      if (translated && translated !== `rewards.actions.${tKey}`)
        return translated;
    }
    return action.action;
  };
  const REDEMPTION_TITLE_TO_KEY: Record<string, string> = {
    'Booking Discount': 'bookingDiscount',
  };
  const getRewardTitle = (reward: { titleKey?: string; title: string }) => {
    if (reward.titleKey) {
      const translated = t(`rewards.redemptionTitle.${reward.titleKey}`);
      if (
        translated &&
        translated !== `rewards.redemptionTitle.${reward.titleKey}`
      )
        return translated;
    }
    const inferredKey = REDEMPTION_TITLE_TO_KEY[reward.title];
    if (inferredKey) {
      const translated = t(`rewards.redemptionTitle.${inferredKey}`);
      if (translated && translated !== `rewards.redemptionTitle.${inferredKey}`)
        return translated;
    }
    return reward.title;
  };
  const getRewardDescription = (reward: {
    descriptionKey?: string;
    description: string;
  }) => {
    if (reward.descriptionKey) {
      const translated = t(`rewards.redemptionDesc.${reward.descriptionKey}`);
      if (
        translated &&
        translated !== `rewards.redemptionDesc.${reward.descriptionKey}`
      )
        return translated;
    }
    if (
      reward.description &&
      /(\d+%?\s*off|next\s*appointment|petso10)/i.test(reward.description)
    ) {
      const translated = t('rewards.redemptionDesc.bookingDiscount');
      if (translated && translated !== 'rewards.redemptionDesc.bookingDiscount')
        return translated;
    }
    return reward.description;
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-[#F7F6FA] dark:bg-[#0F0E16] min-w-0 overflow-hidden">
      {/* Share Options Modal */}
      {showShareModal && (
        <ShareOptionsModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={shareModalContent.title || t('rewards.shareReferralCode')}
          message={
            shareModalContent.message ||
            (userType === 'owner'
              ? t('rewards.joinPetsoOwner', { code: userData.referralCode })
              : t('rewards.joinPetsoProRole', { role: roleConfig?.name || 'professional', code: userData.referralCode }))
          }
          url={
            shareModalContent.url ||
            `${window.location.origin}/join?ref=${userData.referralCode}`
          }
          shareType="referral"
        />
      )}

      <div className="max-w-6xl mx-auto min-w-0">
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 mb-2 min-w-0">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {roleConfig && (
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${roleConfig.color} flex items-center justify-center text-2xl`}
                >
                  {roleConfig.icon}
                </div>
              )}
              <div className="min-w-0">
                <h1
                  className="text-2xl sm:text-3xl text-[#2D2F7E] dark:text-white break-words"
                  style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {roleConfig
                    ? `${roleConfig.roleNameKey ? t(`rewards.roleName.${roleConfig.roleNameKey}`) : roleConfig.name} ${t('rewards.title')}`
                    : t('rewards.petsoRewards')}
                </h1>
                {businessName && (
                  <p
                    className="text-sm text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {businessName}
                  </p>
                )}
              </div>
            </div>
            <DashboardHeaderControls />
          </div>
          <p
            className="text-[#464452] dark:text-white/70 break-words min-w-0"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {roleConfig
              ? t('rewards.earnPointsForServices', {
                  role: t(
                    `rewards.roleName.${professionalType ?? 'other'}`,
                    roleConfig.name
                  ).toLowerCase(),
                })
              : t('rewards.earnPointsEngagement')}
          </p>
        </div>

        {/* Points & Tier Card */}
        <div className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 min-w-0">
            {/* Points Balance */}
            <div className="min-w-0">
              <div
                className="flex items-center gap-2 text-sm mb-2 text-[#464452] dark:text-white/60"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.yourBalance')}
                {chain === 'devnet' && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                    {t('rewards.devnet')}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                {isBalanceLoading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-[#936FEF]" />
                ) : (
                  <span
                    className="text-4xl text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {userData.points.toLocaleString()}
                  </span>
                )}
                <span
                  className="text-[#936FEF] dark:text-purple-400"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  PETS
                </span>
              </div>
              {/* Wallet Address */}
              {walletAddress ? (
                <a
                  href={`https://explorer.solana.com/address/${walletAddress}${getSolanaClusterParam()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#936FEF] hover:text-[#7c5cd4] transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="font-mono">
                    {walletAddress.slice(0, 4)}...{walletAddress.slice(-4)}
                  </span>
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              ) : (
                !isBalanceLoading && (
                  <button
                    onClick={() => {
                      createWallet().then((address) => {
                        if (address) {
                          toast.success(t('rewards.walletCreated'), {
                            description: t('rewards.walletCreatedDesc', {
                              address: `${address.slice(0, 8)}...${address.slice(-4)}`,
                            }),
                          });
                        }
                      });
                    }}
                    disabled={isCreatingWallet}
                    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs bg-[#936FEF] hover:bg-[#7c5cd4] text-white rounded-lg transition-colors disabled:opacity-50"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {isCreatingWallet ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {t('rewards.creating')}
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        {t('rewards.createWallet')}
                      </>
                    )}
                  </button>
                )
              )}
            </div>

            {/* Current Tier */}
            <div className="min-w-0">
              <div
                className="text-sm mb-2 text-[#464452] dark:text-white/60"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.currentTier')}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Award
                  className="w-8 h-8"
                  style={{
                    color: tierColors[userData.tier as keyof typeof tierColors],
                  }}
                />
                <span
                  className="text-2xl text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {userData.tier}
                </span>
              </div>
              <div
                className="text-xs text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {userData.tier === 'Bronze' && t('rewards.tierBronze')}
                {userData.tier === 'Silver' && t('rewards.tierSilver')}
                {userData.tier === 'Gold' && t('rewards.tierGold')}
                {userData.tier === 'Platinum' && t('rewards.tierPlatinum')}
              </div>
            </div>

            {/* Progress to Next Tier */}
            <div className="min-w-0">
              <div
                className="text-sm mb-2 text-[#464452] dark:text-white/60"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.progressTo', { tier: userData.nextTier })}
              </div>
              <div className="mb-2">
                <div className="h-2 bg-white dark:bg-[#0F0E16] rounded-full overflow-hidden border border-[#E2DFEA] dark:border-white/20">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${userData.tierProgress}%`,
                      background: roleConfig
                        ? `linear-gradient(135deg, ${roleConfig.color})`
                        : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                    }}
                  />
                </div>
              </div>
              <div
                className="text-xs text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {userData.pointsToNextTier} {t('rewards.pointsToGo')}
              </div>
            </div>
          </div>
        </div>

        {/* Claim, Send, Transfer Actions */}
        <RewardsActionsSection
          claimableRewards={claimableRewards}
          points={userData.points}
          onShowClaimModal={() => setShowClaimModal(true)}
          onShowSendModal={() => setShowSendModal(true)}
          onShowTransferModal={() => setShowTransferModal(true)}
        />

        {/* Wallet Modals */}
        <RewardsWalletModals
          showClaimModal={showClaimModal}
          onCloseClaim={() => setShowClaimModal(false)}
          claimableRewards={claimableRewards}
          isClaiming={isClaiming}
          onConfirmClaim={async () => {
            const result = await claimRewards();
            if (result?.success) {
              setShowClaimModal(false);
              setShowSuccessScreen('claim');
              toast.success(
                t('rewards.successfullyClaimed', {
                  amount: result.claimedAmount,
                })
              );
            } else {
              toast.error(t('rewards.failedToClaim'));
            }
          }}
          showSendModal={showSendModal}
          onCloseSend={() => setShowSendModal(false)}
          sendAmount={sendAmount}
          setSendAmount={setSendAmount}
          sendRecipient={sendRecipient}
          setSendRecipient={setSendRecipient}
          availableBalance={userData.points}
          onConfirmSend={() => {
            const amount = parseFloat(sendAmount);
            if (amount > 0 && amount <= userData.points) {
              // In future, this will trigger a blockchain transaction
              setSendAmount('');
              setSendRecipient('');
              setShowSendModal(false);
              setShowSuccessScreen('send');
            } else {
              toast.error(t('rewards.invalidAmount'));
            }
          }}
          showTransferModal={showTransferModal}
          onCloseTransfer={() => setShowTransferModal(false)}
          transferAmount={transferAmount}
          setTransferAmount={setTransferAmount}
          selectedWalletType={selectedWalletType}
          setSelectedWalletType={setSelectedWalletType}
          externalWalletAddress={externalWalletAddress}
          setExternalWalletAddress={setExternalWalletAddress}
          isTransferring={isTransferring}
          onConfirmTransfer={async () => {
            const amount = parseFloat(transferAmount);

            // Validate amount
            if (!amount || amount <= 0) {
              toast.error(t('rewards.enterValidAmount'));
              return;
            }

            if (amount > tokenBalance) {
              toast.error(
                t('rewards.insufficientBalance', { balance: tokenBalance })
              );
              return;
            }

            if (!Number.isInteger(amount)) {
              toast.error(t('rewards.amountMustBeWhole'));
              return;
            }

            // Only external wallet transfers are supported via API
            if (selectedWalletType === 'external') {
              if (!externalWalletAddress) {
                toast.error(t('rewards.enterWalletAddress'));
                return;
              }

              // Basic Solana address validation (32-44 chars, base58)
              const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
              if (!solanaAddressRegex.test(externalWalletAddress)) {
                toast.error(t('rewards.invalidSolanaAddress'));
                return;
              }

              const result = await transferToWallet(
                externalWalletAddress,
                amount
              );

              if (result?.success) {
                toast.success(t('rewards.successfullyTransferred', { amount }));
                setTransferAmount('');
                setExternalWalletAddress('');
                setShowTransferModal(false);
                setShowSuccessScreen('transfer');
              } else {
                toast.error(t('rewards.transferFailed'));
              }
            } else {
              toast.info(t('rewards.custodialComingSoon'));
            }
          }}
          showSuccessScreen={showSuccessScreen}
          onCloseSuccess={() => setShowSuccessScreen(null)}
        />

        {/* Tabs - horizontally scrollable on mobile so all tab labels are visible */}
        <div className="bg-white dark:bg-[#16151D] rounded-2xl mb-4 sm:mb-6 border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
          <div
            className="flex overflow-x-auto overflow-y-hidden border-b border-[#E2DFEA] dark:border-white/8 scrollbar-thin scrollbar-thumb-[#E2DFEA] dark:scrollbar-thumb-white/20 scrollbar-track-transparent"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="flex flex-nowrap min-w-0">
              <button
                onClick={() => setActiveTab('earn')}
                className={`flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4 transition-colors whitespace-nowrap touch-manipulation ${
                  activeTab === 'earn'
                    ? 'border-b-2'
                    : 'text-[#464452] dark:text-white/60'
                }`}
                style={
                  activeTab === 'earn'
                    ? { borderColor: '#936FEF', color: '#936FEF' }
                    : {}
                }
              >
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span
                  className="text-sm sm:text-base"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.earnPoints')}
                </span>
              </button>
              {userType === 'owner' && (
                <button
                  onClick={() => setActiveTab('community')}
                  className={`flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4 transition-colors whitespace-nowrap touch-manipulation ${
                    activeTab === 'community'
                      ? 'border-b-2'
                      : 'text-[#464452] dark:text-white/60'
                  }`}
                  style={
                    activeTab === 'community'
                      ? { borderColor: '#936FEF', color: '#936FEF' }
                      : {}
                  }
                >
                  <Star className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <span
                    className="text-sm sm:text-base"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('rewards.tabCommunity')}
                  </span>
                </button>
              )}
              <button
                onClick={() => setActiveTab('referrals')}
                className={`flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4 transition-colors whitespace-nowrap touch-manipulation ${
                  activeTab === 'referrals'
                    ? 'border-b-2'
                    : 'text-[#464452] dark:text-white/60'
                }`}
                style={
                  activeTab === 'referrals'
                    ? { borderColor: '#936FEF', color: '#936FEF' }
                    : {}
                }
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span
                  className="text-sm sm:text-base"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.referrals')}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('redeem')}
                className={`flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4 transition-colors whitespace-nowrap touch-manipulation ${
                  activeTab === 'redeem'
                    ? 'border-b-2'
                    : 'text-[#464452] dark:text-white/60'
                }`}
                style={
                  activeTab === 'redeem'
                    ? { borderColor: '#936FEF', color: '#936FEF' }
                    : {}
                }
              >
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span
                  className="text-sm sm:text-base"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.redeemRewards')}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('claims')}
                className={`flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4 transition-colors whitespace-nowrap touch-manipulation ${
                  activeTab === 'claims'
                    ? 'border-b-2'
                    : 'text-[#464452] dark:text-white/60'
                }`}
                style={
                  activeTab === 'claims'
                    ? { borderColor: '#936FEF', color: '#936FEF' }
                    : {}
                }
              >
                <FileCheck className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span
                  className="text-sm sm:text-base"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.tabClaims')}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 sm:py-4 transition-colors whitespace-nowrap touch-manipulation ${
                  activeTab === 'history'
                    ? 'border-b-2'
                    : 'text-[#464452] dark:text-white/60'
                }`}
                style={
                  activeTab === 'history'
                    ? { borderColor: '#936FEF', color: '#936FEF' }
                    : {}
                }
              >
                <History className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span
                  className="text-sm sm:text-base"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.history')}
                </span>
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 bg-white dark:bg-[#16151D] min-w-0 overflow-hidden">
            {/* Earn Points Tab */}
            {activeTab === 'earn' && (
              <div>
                <h2
                  className="text-xl mb-4 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {roleConfig
                    ? t('rewards.pointsOpportunities', {
                        role: t(
                          `rewards.roleName.${professionalType ?? 'other'}`
                        ),
                      })
                    : t('rewards.waysToEarn')}
                </h2>
                {isLoadingActions ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#936FEF]" />
                  </div>
                ) : earningActions.length === 0 ? (
                  <div
                    className="text-center py-12 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.noEarningActions')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 min-w-0">
                    {earningActions.map((action) => (
                      <div
                        key={action.id}
                        className="rounded-xl p-3 sm:p-4 flex items-center justify-between gap-3 bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/8 min-w-0"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl sm:text-2xl bg-[#F0EFF5] dark:bg-white/5 shrink-0">
                            {action.icon}
                          </div>
                          <div className="min-w-0">
                            <div
                              className="font-semibold mb-0.5 sm:mb-1 text-[#2D2F7E] dark:text-white break-words flex items-center gap-1.5"
                              style={{ fontFamily: 'Sora, sans-serif' }}
                            >
                              {getActionLabel(action)}
                              {action.link && (
                                <a
                                  href={action.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-md hover:bg-[#936FEF]/10 transition-colors shrink-0"
                                  title="Open task link"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 text-[#936FEF] dark:text-purple-400" />
                                </a>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className="text-sm text-[#936FEF] dark:text-purple-400"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                +{action.points} PETS
                              </span>
                              {action.isAutoReward && !action.completed && (
                                <span
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                                  style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                  {t('rewards.automatic')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {action.completed ? (
                          <div
                            className="px-3 py-1.5 rounded-xl text-xs shrink-0 flex items-center gap-1.5"
                            style={{
                              fontFamily: 'Inter, sans-serif',
                              backgroundColor: '#DCFCE7',
                              color: '#059669',
                            }}
                          >
                            <span
                              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px]"
                              style={{ backgroundColor: '#10B981' }}
                            >
                              ✓
                            </span>
                            {t('rewards.rewarded')}
                          </div>
                        ) : action.submissionStatus === 'pending' ? (
                          <span
                            className="px-3 sm:px-4 py-2 rounded-xl text-sm shrink-0 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30"
                            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                          >
                            {t('rewards.pendingReview', 'Pending Review')}
                          </span>
                        ) : action.submissionStatus === 'rejected' ? (
                          <button
                            onClick={() =>
                              handleSubmitAction({
                                actionKey: action.actionKey,
                                action: action.action,
                                points: action.points,
                              })
                            }
                            disabled={submittingActions.has(action.actionKey)}
                            className="px-3 sm:px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity text-sm shrink-0 touch-manipulation disabled:opacity-50"
                            style={{
                              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {submittingActions.has(action.actionKey) ? (
                              <Loader2 className="w-4 h-4 animate-spin inline" />
                            ) : (
                              t('rewards.resubmit', 'Re-submit')
                            )}
                          </button>
                        ) : action.inProgress ? (
                          <button
                            onClick={() =>
                              handleSubmitAction({
                                actionKey: action.actionKey,
                                action: action.action,
                                points: action.points,
                              })
                            }
                            disabled={submittingActions.has(action.actionKey)}
                            className="px-3 sm:px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity text-sm shrink-0 touch-manipulation disabled:opacity-50"
                            style={{
                              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {submittingActions.has(action.actionKey) ? (
                              <Loader2 className="w-4 h-4 animate-spin inline" />
                            ) : (
                              t('rewards.submit', 'Submit')
                            )}
                          </button>
                        ) : action.isAutoReward ? (
                          <div
                            className="px-3 py-1.5 rounded-xl text-xs text-[#464452] dark:text-white/60 border border-[#E2DFEA] dark:border-white/20 shrink-0"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {t('rewards.awardedOnAction')}
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              handleStartAction({
                                actionKey: action.actionKey,
                                action: action.action,
                                points: action.points,
                                link: action.link,
                              })
                            }
                            className="px-3 sm:px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity text-sm shrink-0 touch-manipulation"
                            style={{
                              background: roleConfig
                                ? `linear-gradient(135deg, ${roleConfig.color})`
                                : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {action.isReferral ? t('rewards.viewReferrals') : t('rewards.start')}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Referral CTA */}
                <div
                  className="mt-4 sm:mt-6 rounded-2xl p-4 sm:p-6 min-w-0"
                  style={{
                    background: roleConfig
                      ? `linear-gradient(135deg, ${roleConfig.color})`
                      : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-white">
                    <div className="min-w-0">
                      <h3
                        className="text-lg sm:text-xl mb-1 sm:mb-2 break-words"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 700,
                        }}
                      >
                        {t('rewards.earnMoreReferrals')}
                      </h3>
                      <p
                        className="text-white/90 break-words"
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          opacity: 0.95,
                        }}
                      >
                        {userType === 'owner'
                          ? t('rewards.inviteFriendsOwner')
                          : t('rewards.inviteFriendsPro')}
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveTab('referrals')}
                      className="px-5 py-2.5 sm:px-6 sm:py-3 bg-white dark:bg-white/10 dark:backdrop-blur-sm rounded-xl hover:bg-opacity-90 dark:hover:bg-white/20 transition-opacity flex items-center justify-center gap-2 shrink-0 touch-manipulation"
                      style={{
                        color: '#2D2F7E',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      <span className="dark:text-white">
                        {t('rewards.startReferring')}
                      </span>
                      <ChevronRight className="w-4 h-4 dark:text-white" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Redeem Rewards Tab */}
            {activeTab === 'redeem' && (
              <div>
                <h2
                  className="text-xl mb-4 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {roleConfig
                    ? t('rewards.exclusiveRewards', {
                        role: t(
                          `rewards.roleName.${professionalType ?? 'other'}`,
                          roleConfig.name
                        ),
                      })
                    : t('rewards.redeemYourPoints')}
                </h2>
                {userType === 'owner' && redeemRewardsLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-[#936FEF]" />
                  </div>
                )}
                {userType === 'owner' &&
                  !redeemRewardsLoading &&
                  redemptions.length === 0 && (
                    <div
                      className="rounded-xl border border-dashed p-8 text-center"
                      style={{
                        borderColor: 'var(--border, #E2DFEA)',
                        color: 'var(--text-secondary, #464452)',
                      }}
                    >
                      <Gift className="w-12 h-12 mx-auto mb-3 opacity-50 text-[#936FEF]" />
                      <p
                        className="text-[#464452] dark:text-white/70"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t('rewards.noRedeemableRewards')}
                      </p>
                    </div>
                  )}
                {redemptions.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 min-w-0">
                    {redemptions.map((reward) => {
                      const canAfford =
                        pointsAvailableForRedeem >= reward.points;
                      const cappedOut =
                        'remainingQuantity' in reward &&
                        reward.remainingQuantity !== undefined &&
                        reward.remainingQuantity <= 0;
                      const disabled = !canAfford || cappedOut;
                      const apiReward =
                        reward.type === 'api'
                          ? apiRedeemRewards.find((x) => x.id === reward.id)
                          : null;
                      const displayPercent =
                        apiReward?.discountPercent ??
                        apiReward?.discount_percent ??
                        (reward as { discountPercent?: number })
                          .discountPercent;
                      const getCode = (obj: unknown): string | null => {
                        if (obj == null || typeof obj !== 'object') return null;
                        const o = obj as Record<string, unknown>;
                        const v = o.discountCode ?? o.discount_code;
                        if (typeof v === 'string') {
                          const s = v.trim();
                          return s.length > 0 ? s : null;
                        }
                        if (typeof v === 'number')
                          return String(v).trim() || null;
                        return null;
                      };
                      const parseCodeFromDescription = (
                        desc: string
                      ): string | null => {
                        if (!desc || typeof desc !== 'string') return null;
                        const trimmed = desc.trim();
                        const useCode = trimmed.match(
                          /use\s+code\s*:?\s*([A-Za-z0-9]{3,25})/i
                        );
                        if (useCode) return useCode[1].trim();
                        const withCode = trimmed.match(
                          /(?:code|promo)\s*:?\s*([A-Za-z0-9]{3,25})/i
                        );
                        if (withCode) return withCode[1].trim();
                        const allCaps = trimmed.match(
                          /\b([A-Z][A-Z0-9]{3,20})\b/
                        );
                        if (allCaps) return allCaps[1];
                        return null;
                      };
                      const descForCode = (apiReward?.description ??
                        reward.description ??
                        '') as string;
                      const displayCode =
                        getCode(apiReward) ??
                        getCode(reward) ??
                        parseCodeFromDescription(descForCode) ??
                        null;
                      const descPercent =
                        displayPercent == null &&
                        typeof reward.description === 'string' &&
                        reward.description.length > 0
                          ? (() => {
                              const match =
                                reward.description.match(/(\d+)\s*%\s*off/i) ??
                                reward.description.match(/(\d+)\s*%/);
                              return match ? parseInt(match[1], 10) : null;
                            })()
                          : null;
                      const effectivePercent = displayPercent ?? descPercent;
                      const showDiscountPercent =
                        effectivePercent != null && effectivePercent >= 0;
                      return (
                        <div
                          key={reward.id}
                          className="rounded-xl p-4 sm:p-6 bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div
                              className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl overflow-hidden shrink-0"
                              style={{
                                background: roleConfig
                                  ? `linear-gradient(135deg, ${roleConfig.color})`
                                  : 'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
                              }}
                            >
                              {'imageUrl' in reward && reward.imageUrl ? (
                                <img
                                  src={reward.imageUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                (reward.icon ?? '🎁')
                              )}
                            </div>
                            <div
                              className="px-3 py-1 rounded-full text-sm bg-[#F0EFF5] dark:bg-purple-900/20 text-[#936FEF] dark:text-purple-300"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 700,
                              }}
                            >
                              {reward.points} PETS
                            </div>
                          </div>
                          <h3
                            className="mb-2 text-[#2D2F7E] dark:text-white"
                            style={{
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {getRewardTitle(reward)}
                          </h3>
                          <p
                            className="text-sm mb-4 text-[#464452] dark:text-white/70"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {getRewardDescription(reward)}
                          </p>
                          {'remainingQuantity' in reward &&
                            reward.remainingQuantity !== undefined &&
                            reward.remainingQuantity !== null && (
                              <p className="text-xs mb-2 text-[#464452] dark:text-white/60">
                                {reward.remainingQuantity === 0
                                  ? t('rewards.noLongerAvailable')
                                  : t('rewards.left', {
                                      count: reward.remainingQuantity,
                                    })}
                              </p>
                            )}
                          {showDiscountPercent && effectivePercent != null && (
                            <p
                              className="text-sm mb-2 font-medium text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Sora, sans-serif' }}
                            >
                              {effectivePercent}% off
                            </p>
                          )}
                          {showDiscountPercent && effectivePercent != null && (
                            <div className="mb-3 px-2.5 py-2 rounded-lg bg-[#F0EFF5] dark:bg-white/10 border border-[#E2DFEA] dark:border-white/10">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className="text-xs text-[#464452] dark:text-white/60 shrink-0"
                                  style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                  {t('rewards.useAtBooking')}
                                </span>
                                <code
                                  className="inline-block px-2.5 py-1 rounded-md bg-white dark:bg-white/10 text-xs font-semibold text-[#2D2F7E] dark:text-white tracking-wide border border-[#E2DFEA] dark:border-white/15"
                                  style={{ fontFamily: 'Sora, sans-serif' }}
                                >
                                  {displayCode || '—'}
                                </code>
                                {displayCode ? (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await copyToClipboard(displayCode);
                                      toast.success(t('rewards.codeCopied'));
                                    }}
                                    className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#936FEF] text-white text-xs font-medium hover:opacity-90 active:scale-[0.98] transition-all"
                                    style={{ fontFamily: 'Sora, sans-serif' }}
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                    {t('rewards.dashboard.copy')}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          )}
                          <button
                            onClick={() => handleRedeemReward(reward)}
                            disabled={disabled}
                            className={`w-full px-4 py-2 rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mb-3`}
                            style={{
                              background: roleConfig
                                ? `linear-gradient(135deg, ${roleConfig.color})`
                                : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {cappedOut
                              ? t('rewards.soldOut', 'Sold out')
                              : canAfford
                                ? t('rewards.redeem')
                                : t('rewards.needMore', {
                                    count: reward.points - userData.points,
                                  })}
                          </button>

                          {/* Share Section - Always visible */}
                          <div className="border-t pt-3 border-[#E2DFEA] dark:border-white/8">
                            <div className="flex items-center justify-between mb-2">
                              <span
                                className="text-xs text-[#464452] dark:text-white/60"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {redeemedRewards.includes(reward.id) ? (
                                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                                    <Star className="w-3 h-3" />
                                    {t(
                                      'rewards.dashboard.redeemedShareAchievement'
                                    )}
                                  </span>
                                ) : (
                                  t('rewards.dashboard.shareRewardWithFriends')
                                )}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                const roleText = roleConfig
                                  ? `as a ${roleConfig.name}`
                                  : '';
                                setShareModalContent({
                                  title: t(
                                    'rewards.dashboard.shareRewardTitle'
                                  ),
                                  message: `🎉 I just redeemed "${reward.title}" ${roleText} on Petso! Earning rewards for providing great pet care. 🐾 #Petso #PetCare #Rewards`,
                                  url: `${window.location.origin}/rewards?ref=${userData.referralCode}`,
                                });
                                setShowShareModal(true);
                              }}
                              className="w-full px-3 py-2 bg-gray-100 dark:bg-white/5 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors flex items-center justify-center gap-2 text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              <Share2 className="w-4 h-4" />
                              <span className="text-sm">
                                {t('rewards.referralsTab.share')}
                              </span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <div>
                <h2
                  className="text-xl mb-4 text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.referralsTab.referAndEarn')}
                </h2>

                {/* Referral Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6 min-w-0">
                  {referralStatsDisplay.map((stat) => {
                    const Icon = stat.icon;
                    return (
                      <div
                        key={stat.label}
                        className="rounded-xl p-4 sm:p-6 bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/8 min-w-0"
                      >
                        <Icon
                          className="w-8 h-8 mb-3"
                          style={{ color: stat.color }}
                        />
                        <div
                          className="text-3xl mb-1 text-[#2D2F7E] dark:text-white"
                          style={{
                            fontFamily: 'Sora, sans-serif',
                            fontWeight: 700,
                          }}
                        >
                          {stat.value}
                        </div>
                        <div
                          className="text-sm text-[#464452] dark:text-white/70"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {stat.label}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Referral Code */}
                <div className="rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
                  <div
                    className="text-sm mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.referralsTab.yourReferralCode')}
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 min-w-0">
                    <div
                      className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white break-all"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                        fontSize: '16px',
                      }}
                    >
                      {userData.referralCode}
                    </div>
                    <div className="flex gap-2 flex-wrap shrink-0">
                      <button
                        onClick={handleCopyReferralCode}
                        className="px-4 sm:px-6 py-3 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 border bg-[#F0EFF5] dark:bg-white/5 border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white touch-manipulation"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 700,
                        }}
                      >
                        {copied ? (
                          <>
                            <Check className="w-5 h-5" />
                            {t('rewards.referralsTab.copied')}
                          </>
                        ) : (
                          <>
                            <Copy className="w-5 h-5" />
                            {t('rewards.referralsTab.copy')}
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowShareModal(true)}
                        className="px-4 sm:px-6 py-3 rounded-xl hover:opacity-90 transition-opacity flex items-center gap-2 border bg-[#F0EFF5] dark:bg-white/5 border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white touch-manipulation"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 700,
                        }}
                      >
                        <Share2 className="w-5 h-5" />
                        {t('rewards.referralsTab.share')}
                      </button>
                    </div>
                  </div>

                  {/* Shareable link for pet owners (Petso Webapp) — from Admin → Rewards → Referrals */}
                  <div
                    className="text-sm mt-4 mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.referralsTab.shareableLinkForOwners')}
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 min-w-0">
                    <div
                      className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white overflow-x-auto overflow-y-hidden"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      <span className="block whitespace-nowrap select-all">
                        {webappLinkUrl}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopyReferralLink('webapp')}
                      className="px-4 sm:px-6 py-3 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 border bg-[#F0EFF5] dark:bg-white/5 border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white flex-shrink-0 touch-manipulation"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {linkCopiedWhich === 'webapp' ? (
                        <>
                          <Check className="w-5 h-5" />
                          {t('rewards.referralsTab.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          {t('rewards.referralsTab.copyLink')}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Shareable link for pet professionals (Petso Pro) — from Admin → Rewards → Referrals */}
                  <div
                    className="text-sm mt-4 mb-2 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.referralsTab.shareableLinkForProfessionals')}
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 min-w-0">
                    <div
                      className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white overflow-x-auto overflow-y-hidden"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      <span className="block whitespace-nowrap select-all">
                        {petsoProLinkUrl}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopyReferralLink('pro')}
                      className="px-4 sm:px-6 py-3 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 border bg-[#F0EFF5] dark:bg-white/5 border-[#E2DFEA] dark:border-white/20 text-[#2D2F7E] dark:text-white flex-shrink-0 touch-manipulation"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {linkCopiedWhich === 'pro' ? (
                        <>
                          <Check className="w-5 h-5" />
                          {t('rewards.referralsTab.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          {t('rewards.referralsTab.copyLink')}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* How It Works */}
                <div className="rounded-xl p-6 bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/8">
                  <h3
                    className="mb-4 text-[#2D2F7E] dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('rewards.referralsTab.howReferralsWork')}
                  </h3>
                  <div
                    className="space-y-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {userType === 'owner' ? (
                      <>
                        <div className="flex gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                            style={{
                              background:
                                'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                            }}
                          >
                            1
                          </div>
                          <div>
                            <div className="font-semibold text-[#2D2F7E] dark:text-white">
                              {t('rewards.referralsTab.inviteOwner')}
                            </div>
                            <div className="text-sm text-[#464452] dark:text-white/70">
                              {t('rewards.earnWhenCreatePet', { points: referralConfig?.ownerCompletedPoints ?? 200 })}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                            style={{
                              background:
                                'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                            }}
                          >
                            2
                          </div>
                          <div>
                            <div className="font-semibold text-[#2D2F7E] dark:text-white">
                              {t('rewards.referralsTab.inviteProfessional')}
                            </div>
                            <div className="text-sm text-[#464452] dark:text-white/70">
                              {t('rewards.earnWhenFirstBooking', { points: referralConfig?.professionalCompletedPoints ?? 300 })}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                            style={{
                              background: roleConfig
                                ? `linear-gradient(135deg, ${roleConfig.color})`
                                : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                            }}
                          >
                            1
                          </div>
                          <div>
                            <div className="font-semibold text-[#2D2F7E] dark:text-white">
                              {t('rewards.referralsTab.invitePetOwner')}
                            </div>
                            <div className="text-sm text-[#464452] dark:text-white/70">
                              {t('rewards.earnWhenBookWithYou', { points: referralConfig?.ownerCompletedPoints ?? 200, role: roleConfig?.name?.toLowerCase() ?? 'practice' })}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                            style={{
                              background: roleConfig
                                ? `linear-gradient(135deg, ${roleConfig.color})`
                                : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                            }}
                          >
                            2
                          </div>
                          <div>
                            <div className="font-semibold text-[#2D2F7E] dark:text-white">
                              {t(
                                'rewards.referralsTab.inviteAnotherProfessional'
                              )}
                            </div>
                            <div className="text-sm text-[#464452] dark:text-white/70">
                              {t('rewards.earnWhenFirstBooking', { points: referralConfig?.professionalCompletedPoints ?? 300 })}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Community Rewards Tab (Pet Owners Only) */}
            {activeTab === 'community' && userType === 'owner' && (
              <CommunityRewards />
            )}

            {/* Claims Tab - Airdrops and ICO Claims */}
            {activeTab === 'claims' && <ClaimsView />}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="min-w-0">
                <h2
                  className="text-lg sm:text-xl mb-3 sm:mb-4 text-[#2D2F7E] dark:text-white break-words"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('rewards.dashboard.pointsHistory')}
                </h2>
                {isLoadingHistory && historyTransactions.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#936FEF]" />
                  </div>
                ) : historyTransactions.length === 0 ? (
                  <div
                    className="text-center py-12 text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.dashboard.noTransactionHistory')}
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 min-w-0">
                      {historyTransactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          className="rounded-xl p-3 sm:p-4 flex items-center justify-between gap-3 bg-white dark:bg-[#0F0E16] border border-[#E2DFEA] dark:border-white/8 min-w-0"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0"
                              style={{
                                backgroundColor:
                                  transaction.type === 'earned' ||
                                  transaction.type === 'claimed'
                                    ? '#10B981'
                                    : '#936FEF',
                              }}
                            >
                              {transaction.type === 'earned' ||
                              transaction.type === 'claimed'
                                ? '+'
                                : '−'}
                            </div>
                            <div className="min-w-0">
                              <div
                                className="font-semibold mb-0.5 sm:mb-1 text-[#2D2F7E] dark:text-white break-words"
                                style={{ fontFamily: 'Sora, sans-serif' }}
                              >
                                {transaction.description}
                              </div>
                              <div
                                className="text-sm text-[#464452] dark:text-white/70"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                {formatHistoryDate(transaction.createdAt)}
                              </div>
                            </div>
                          </div>
                          <div
                            className="text-lg sm:text-xl shrink-0"
                            style={{
                              color:
                                transaction.type === 'earned' ||
                                transaction.type === 'claimed'
                                  ? '#10B981'
                                  : '#936FEF',
                              fontFamily: 'Sora, sans-serif',
                              fontWeight: 700,
                            }}
                          >
                            {transaction.points > 0 ? '+' : ''}
                            {transaction.points} PETS
                          </div>
                        </div>
                      ))}
                    </div>

                    {historyHasMore && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={handleLoadMoreHistory}
                          disabled={isLoadingHistory}
                          className="px-6 py-2 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors border border-[#E2DFEA] dark:border-white/20 text-[#464452] dark:text-white/70 disabled:opacity-50"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {isLoadingHistory ? (
                            <span className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t('rewards.dashboard.loading')}
                            </span>
                          ) : (
                            t('rewards.dashboard.loadMore')
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="rounded-2xl p-4 sm:p-6 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
          <h3
            className="mb-3 text-[#2D2F7E] dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            ℹ️{' '}
            {roleConfig
              ? t('rewards.dashboard.aboutPetsoRewardsPro', {
                  role: roleConfig.name,
                })
              : t('rewards.dashboard.aboutPetsoRewards')}
          </h3>
          <ul
            className="space-y-2 text-sm text-[#464452] dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <li>• {t('rewards.dashboard.aboutBullet1')}</li>
            <li>
              •{' '}
              {roleConfig
                ? t('rewards.dashboard.aboutBullet2Pro', {
                    role: roleConfig.name.toLowerCase(),
                  })
                : t('rewards.dashboard.aboutBullet2')}
            </li>
            <li>• {t('rewards.dashboard.aboutBullet3')}</li>
            <li>• {t('rewards.dashboard.aboutBullet4')}</li>
            <li>• {t('rewards.dashboard.aboutBullet5')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
